// @givmo/board — the conflict-of-interest slice's tolerant decode.
//
// ══ GENERATED TYPES DO NOT REPLACE RUNTIME DECODING ═════════════════════════════════════════════
//
// All eight conflict operations and their row types are now in the committed OpenAPI client. The
// generated types are the compile-time contract, but they do not validate the bytes that arrive. This
// module therefore still reads each field defensively and is applied in every generated query's
// `select`, so the cache keeps the generated response while each observer receives the tolerant view.
//
// The exported row aliases below point at the generated models; there is no second hand-written wire
// contract. Unknown future enum values are preserved at runtime and flow through the open display
// helpers rather than being coerced to a value this build knows.
//
// ══ WHAT THIS MODULE DELIBERATELY DOES NOT DO ══════════════════════════════════════════════════
//
// There is **no matching, no scoring, no ranking, no sorting by relevance and no filtering** here.
// Not because it would be hard, but because each one would make the client an author of the very
// thing plan §3.2 admits only inside a confirm-or-correct control. Concretely:
//
//   * The SUGGESTIONS come from the backend's whole-token match over counterparty names. Re-ordering
//     them client-side would present a machine ranking as though a human had prioritised something.
//   * The MOTION's links arrive already narrowed to CONFIRMATIONS (`board_minutes_service.
//     _conflict_links_out`). Filtering them again here would be a second, drifting definition of what
//     appears before a vote — and filtering them the WRONG way puts a dismissal in front of the board,
//     which is precisely the thing the officer recorded as not being there.
//   * No count, no total, no "N conflicts" derived from row length. Quorum, votes, presence and
//     conflicts are all figures the server states; a client that adds rows up has invented one.
//
// Every sentence a reader sees about a link or a candidate is the backend's own `statement`, rendered
// verbatim. The two enum-ish fields (`status`, `link_target`, `recorded_via`) are typed OPEN — a value
// this build has not heard of renders through `humanize` rather than being coerced away.

import type {
  OrgBoardConflictDisclosureOut,
  BoardConflictLinkOut,
  BoardConflictSuggestionOut,
} from '@givmo/api-client';
import { displayInstant, humanize } from '../../lib/display';
import { factorNoun } from '../../lib/factor-words';
import { officerRolesWords as roles } from '../roster/officer-roles-words';
import { annualOf } from './coi-decode';

/** The decoder materializes every optional field the view reads, normalizing absence to `null`. */
type Present<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

/** Narrow an unknown value to a plain object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A required string field, or `''` when the wire carried something else. */
function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** An optional string field, normalised so `''` and a whitespace-only value both read as absent. */
function optionalText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * One recorded disclosure, as the register and both disclosure doors render it.
 *
 * Mirrors the backend's `OrgBoardConflictDisclosureOut` without its organization key, which nothing
 * here reads: the row stays the same whichever door's wire it was decoded from. `status` is DERIVED
 * server-side from the version pointers (`active` while no later disclosure by that seat names this
 * one, `superseded` once one does) — it is not stored, and nothing here re-derives it.
 */
export type ConflictDisclosureRow = Present<
  Omit<OrgBoardConflictDisclosureOut, 'org_ref'>,
  | 'annual'
  | 'capture_method'
  | 'content_hash'
  | 'corrects_record_id'
  | 'counterparty_name'
  | 'disclosed_on'
  | 'disclosing_member_name'
  | 'meeting_record_id'
  | 'recorded_at'
  | 'superseded_by_record_id'
  | 'supersedes_record_id'
>;

/**
 * One recorded link between a disclosure and an agenda item or a motion.
 *
 * ⚠ `recusal_recorded_at` is a DATE, and its absence is a statement about the book — "no recusal for
 * that seat has been recorded on this motion" — never a verdict about whether one ought to have been.
 * The backend is explicit that the field's null means two different things depending on where the row
 * came from (beside a motion: no such recusal recorded; on the links list: there is no motion in
 * question at all), and that `statement` is the disambiguator. So this app renders the SENTENCE and
 * uses the instant only where the meeting supplies a zone to render it in.
 */
export type ConflictLinkRow = Present<
  BoardConflictLinkOut,
  | 'agenda_item_id'
  | 'counterparty_name'
  | 'disclosed_on'
  | 'disclosing_member_name'
  | 'motion_id'
  | 'note'
  | 'recorded_at'
  | 'recusal_recorded_at'
>;

/** One candidate the officer may confirm or dismiss. Never stored — it has no id, on purpose. */
export type ConflictSuggestionRow = Present<
  BoardConflictSuggestionOut,
  'agenda_item_id' | 'disclosed_on' | 'disclosing_member_name' | 'motion_id'
>;

/**
 * The suggestion read, with the method statement that must travel with it.
 *
 * ⚠ `method` is `null` when the response carried none, and that is NOT the same as an empty list of
 * candidates. The dangerous reading of an empty result is "the module checked and found nothing to
 * disclose"; the only thing that forecloses it is the honest statement of what was and was not looked
 * at, shown beside the answer. A response with no method is therefore not renderable as suggestions —
 * see `conflicts-panel.tsx`, which shows the read as unavailable rather than showing candidates (or,
 * worse, an authoritative-looking emptiness) without it.
 */
export interface ConflictSuggestionList {
  method: string | null;
  suggestions: ConflictSuggestionRow[];
}

function decodeDisclosure(value: unknown): ConflictDisclosureRow | null {
  if (!isRecord(value)) return null;
  const recordId = text(value.record_id);
  if (recordId.length === 0) return null;
  return {
    record_id: recordId,
    disclosing_board_member_id: text(value.disclosing_board_member_id),
    disclosing_member_name: optionalText(value.disclosing_member_name),
    disclosed_on: optionalText(value.disclosed_on),
    interest_summary: text(value.interest_summary),
    counterparty_name: optionalText(value.counterparty_name),
    title: text(value.title),
    // Keep an unknown future value at runtime. Display helpers accept `string` and humanize it.
    status: text(value.status) as OrgBoardConflictDisclosureOut['status'],
    supersedes_record_id: optionalText(value.supersedes_record_id),
    superseded_by_record_id: optionalText(value.superseded_by_record_id),
    corrects_record_id: optionalText(value.corrects_record_id),
    meeting_record_id: optionalText(value.meeting_record_id),
    content_hash: optionalText(value.content_hash),
    capture_method: optionalText(value.capture_method),
    recorded_at: optionalText(value.recorded_at),
    statement: text(value.statement),
    // CBG-YR2-WEB1: an annual response's year and the interests its answers name (§10.1); null on
    // every single disclosure, and on an answer from before the field existed.
    annual: annualOf(value),
  };
}

function decodeLink(value: unknown): ConflictLinkRow | null {
  if (!isRecord(value)) return null;
  const id = text(value.id);
  if (id.length === 0) return null;
  return {
    id,
    meeting_id: text(value.meeting_id),
    disclosure_record_id: text(value.disclosure_record_id),
    link_target: text(value.link_target) as BoardConflictLinkOut['link_target'],
    agenda_item_id: optionalText(value.agenda_item_id),
    motion_id: optionalText(value.motion_id),
    status: text(value.status) as BoardConflictLinkOut['status'],
    recorded_via: text(value.recorded_via) as BoardConflictLinkOut['recorded_via'],
    note: optionalText(value.note),
    recorded_at: optionalText(value.recorded_at),
    disclosing_board_member_id: text(value.disclosing_board_member_id),
    disclosing_member_name: optionalText(value.disclosing_member_name),
    disclosed_on: optionalText(value.disclosed_on),
    counterparty_name: optionalText(value.counterparty_name),
    recusal_recorded_at: optionalText(value.recusal_recorded_at),
    statement: text(value.statement),
  };
}

function decodeSuggestion(value: unknown): ConflictSuggestionRow | null {
  if (!isRecord(value)) return null;
  const disclosureId = text(value.disclosure_record_id);
  if (disclosureId.length === 0) return null;
  return {
    disclosure_record_id: disclosureId,
    disclosing_board_member_id: text(value.disclosing_board_member_id),
    disclosing_member_name: optionalText(value.disclosing_member_name),
    disclosed_on: optionalText(value.disclosed_on),
    matched_name: text(value.matched_name),
    link_target: text(value.link_target) as BoardConflictSuggestionOut['link_target'],
    agenda_item_id: optionalText(value.agenda_item_id),
    motion_id: optionalText(value.motion_id),
    statement: text(value.statement),
  };
}

/** Read a list body in either the bare-array or `{data:[…]}` shape. Never throws. */
function rows(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isRecord(body) && Array.isArray(body.data)) return body.data;
  return [];
}

/** The register, or a director's own disclosures. Order is the backend's; nothing re-sorts. */
export function extractDisclosures(body: unknown): ConflictDisclosureRow[] {
  return rows(body).flatMap((row) => {
    const decoded = decodeDisclosure(row);
    return decoded === null ? [] : [decoded];
  });
}

/** Every decision recorded for a meeting — confirmations AND dismissals, as the backend returns them. */
export function extractLinks(body: unknown): ConflictLinkRow[] {
  return rows(body).flatMap((row) => {
    const decoded = decodeLink(row);
    return decoded === null ? [] : [decoded];
  });
}

/**
 * The suggestion read.
 *
 * A body that is not an object at all yields `{ method: null, suggestions: [] }` — which the panel
 * renders as "we could not show this", never as "there is nothing".
 */
export function decodeSuggestionList(body: unknown): ConflictSuggestionList {
  if (!isRecord(body)) return { method: null, suggestions: [] };
  const suggestions = Array.isArray(body.suggestions)
    ? body.suggestions.flatMap((row) => {
        const decoded = decodeSuggestion(row);
        return decoded === null ? [] : [decoded];
      })
    : [];
  return { method: optionalText(body.method), suggestions };
}

/**
 * The confirmed links the motion read already carries, off one motion row.
 *
 * ⚠ Taken from the motion VERBATIM and not narrowed. `board_minutes_service._conflict_links_out` has
 * already selected the confirmations and resolved each one's disclosed facts under the reader's own
 * confidentiality scope, so a second opinion here could only ever disagree with the record. A link
 * whose disclosure this reader may not read is already absent by the time it arrives — rendered as
 * nothing at all rather than as a half-filled entry.
 *
 * Read off `unknown`, tolerantly, from `BoardMotionOut.conflict_disclosure_links`: a motion that
 * carries none, a `null`, or a list this build cannot read yields no links.
 */
export function motionConflictLinks(motion: unknown): ConflictLinkRow[] {
  if (!isRecord(motion)) return [];
  return extractLinks(motion.conflict_disclosure_links);
}

// ── Labels ───────────────────────────────────────────────────────────────────────────────────────
//
// Each ends on `humanize` so a token a later backend adds still renders as something readable rather
// than vanishing. None of them states a conclusion: they name what was recorded, in the register's own
// vocabulary.

/** Where a disclosure stands in its own chain: "Current" (CBG-UX-COPY2's A5c, beside the approved
 *  "Replaced by a later disclosure"). */
export function disclosureStatusLabel(status: string): string {
  if (status === 'active') return 'Current';
  if (status === 'superseded') return 'Replaced by a later disclosure';
  return humanize(status);
}

export function disclosureStatusTone(status: string): 'success' | 'neutral' {
  return status === 'active' ? 'success' : 'neutral';
}

/** What the officer recorded about a candidate. */
export function linkStatusLabel(status: string): string {
  if (status === 'confirmed') return 'You recorded this link';
  if (status === 'dismissed') return 'You recorded that it does not apply';
  return humanize(status);
}

export function linkStatusTone(status: string): 'info' | 'neutral' {
  return status === 'confirmed' ? 'info' : 'neutral';
}

/** Which object a link names. Kept distinct even where an agenda-item link surfaces beside a motion. */
export function linkTargetLabel(target: string): string {
  if (target === 'agenda_item') return 'An agenda item';
  if (target === 'motion') return 'A motion';
  return humanize(target);
}

/** How the officer came to record it — provenance about their act, never a claim about the link. */
export function recordedViaLabel(via: string): string {
  if (via === 'officer_entry') return 'You named it yourself';
  if (via === 'confirmed_suggestion')
    return 'You confirmed a candidate that was put in front of you';
  return humanize(via);
}

/** Whose page an entry's line is read on: the officer's register, about someone else, or the
 *  person's own page, said to them (CBG-UX-COPY2's A8). */
export type DisclosureReader = 'officer' | 'own';

/**
 * How a person's own entry was captured, where the backend recorded one.
 *
 * `manual_on_behalf` is the officer's door; the others are the second factor the person completed
 * inside their own session. Absent for a record whose approval event carried none. Everyone on the
 * roster files a disclosure, director or not (CBG-YR3-ROLES), so neither line says "the director".
 * On the person's own page (`own`) the line is said to them: "You confirmed it with a passkey",
 * "Recorded by an officer on your behalf".
 */
export function captureMethodLabel(
  method: string | null,
  reader: DisclosureReader = 'officer',
): string | null {
  if (method === null) return null;
  const onBehalf = reader === 'own' ? roles.officerOnYourBehalf : roles.officerOnTheirBehalf;
  if (method === 'manual_on_behalf') return `Recorded ${onBehalf}`;
  const factor = factorNoun(method);
  // The one set of factor words (CBG-UX3): "…with a code sent by email", as the consent log says.
  if (factor !== null) {
    return reader === 'own'
      ? roles.disclosureYouConfirmed(factor)
      : roles.disclosureOwnSession(factor);
  }
  return humanize(method);
}

/**
 * When the entry was made, and how the person's own entry was captured, as ONE line (CBG-UX4).
 *
 * `recorded_at` is when the ENTRY was made, in the reader's own zone. The capture follows it; an
 * officer's entry reads "Recorded <when> by an officer on their behalf" ("on your behalf" on the
 * person's own page), because "Recorded <when> · Recorded by an officer…" said the verb twice. With
 * no capture recorded, the line is only the time.
 */
export function disclosureRecordedLine(
  recordedAt: string | null | undefined,
  method: string | null,
  reader: DisclosureReader = 'officer',
): string {
  const when = `Recorded ${displayInstant(recordedAt)}`;
  if (method === 'manual_on_behalf') {
    return `${when} ${reader === 'own' ? roles.officerOnYourBehalf : roles.officerOnTheirBehalf}`;
  }
  const capture = captureMethodLabel(method, reader);
  return capture !== null ? `${when} · ${capture}` : when;
}
